import React, { useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { uploadItemImage } from '../../apis/atlantis.api';

const IMAGE_TYPE_MAP = {
  'shop-hour-button-image': 'button',
  'shop-hour-description-image': 'banner',
  'shop-hour-bg-desktop': 'bg-desktop',
  'shop-hour-bg-mobile': 'bg-mobile',
  'add-shop-item-image': 'item',
  'edit-shop-item-image': 'item',
};

const ImageUploadField = ({
  value,
  onChange,
  token,
  label,
  id,
  showPreview = true,
  showSuccessIndicator = true,
}) => {
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showManualUrl, setShowManualUrl] = useState(false);

  const processFile = async file => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please select a valid image file (.png, .jpg, .jpeg)');
      setShowManualUrl(true);
      return;
    }

    const MAX_FILE_SIZE = 4.5 * 1024 * 1024; // 4.5MB
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File too large. Please use an image under 4.5MB.');
      return;
    }

    setUploadLoading(true);
    setUploadError('');
    setUploadSuccess(false);

    try {
      const imageType = IMAGE_TYPE_MAP[id];
      const imageUrl = await uploadItemImage(file, imageType, token);
      if (imageUrl) {
        onChange(imageUrl);
        setUploadSuccess(true);
      } else {
        setUploadError('Failed to upload image. Please try again.');
        setShowManualUrl(true);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadError(error.message || 'Failed to upload image. Please try again.');
      setShowManualUrl(true);
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileUpload = async event => {
    const file = event.target.files[0];
    if (!file) return;
    processFile(file);
  };

  const handleDragOver = e => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  return (
    <div className="mb-4">
      {label && (
        <Form.Label className="text-muted fw-medium mb-2" style={{ fontSize: '0.875rem' }}>
          {label}
        </Form.Label>
      )}

      <div
        onClick={() => !uploadLoading && document.getElementById(id).click()}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{
          border: '2px dashed',
          borderColor: isDragging ? '#6366f1' : '#dee2e6',
          backgroundColor: isDragging ? 'rgba(99, 102, 241, 0.05)' : '#f8f9fa',
          borderRadius: '0.75rem',
          padding: '2rem',
          textAlign: 'center',
          cursor: uploadLoading ? 'default' : 'pointer',
          transition: 'all 0.2s ease',
          position: 'relative',
        }}
      >
        {uploadLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '0.75rem',
            }}
          >
            <Spinner animation="border" role="status" style={{ color: '#6366f1' }}>
              <span className="visually-hidden">Uploading...</span>
            </Spinner>
          </div>
        )}
        <input
          type="file"
          id={id}
          accept=".png,.jpg,.jpeg"
          onChange={handleFileUpload}
          disabled={uploadLoading}
          style={{ display: 'none' }}
        />
        <div style={{ color: '#6c757d' }}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="48"
            height="48"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginBottom: '0.5rem', opacity: 0.6 }}
          >
            <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z" />
            <path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z" />
          </svg>
          <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>Drag & drop image here</div>
          <div style={{ fontSize: '0.875rem' }}>or click to browse</div>
          <div style={{ fontSize: '0.75rem', marginTop: '0.5rem', opacity: 0.7 }}>
            Accepts .png, .jpg, .jpeg (max 4.5MB)
          </div>
        </div>
      </div>

      {showSuccessIndicator && uploadSuccess && !uploadLoading && (
        <div
          className="mt-2 d-flex align-items-center"
          style={{ color: '#22c55e', fontSize: '0.875rem' }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            fill="currentColor"
            viewBox="0 0 16 16"
            style={{ marginRight: '0.375rem' }}
          >
            <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
          </svg>
          Upload successful!
        </div>
      )}

      {uploadError && (
        <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
          {uploadError}
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setShowManualUrl(!showManualUrl)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: '#6c757d',
            fontSize: '0.875rem',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          {showManualUrl ? 'Hide URL input' : 'Having trouble? Enter URL manually'}
        </button>
      </div>

      {showManualUrl && (
        <div className="mt-3">
          <Form.Control
            type="url"
            placeholder="Paste image URL here"
            value={value || ''}
            onChange={e => onChange(e.target.value)}
          />
        </div>
      )}

      {showPreview && value && (
        <div className="mt-3 p-3 bg-light rounded-3 text-center">
          <img
            src={value}
            alt={label || 'Preview'}
            className="img-fluid rounded-3 shadow-sm"
            style={{ maxHeight: '250px', objectFit: 'contain' }}
          />
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
